(() => {
  function clampIndex(index, length) {
    if (!Number.isFinite(index)) return 0;
    if (length <= 0) return 0;
    if (index < 0) return 0;
    if (index > length - 1) return length - 1;
    return index;
  }

  function bindExternalNav(controller, node) {
    const selector = node.dataset.externalNav;
    if (!selector) return;

    const sectionRoot = node.closest('.shopify-section');
    const nav = (sectionRoot && sectionRoot.querySelector(selector)) || document.querySelector(selector);
    if (!nav) return;

    const buttons = Array.from(nav.querySelectorAll('[data-slide]'));
    if (!buttons.length) return;

    const syncNavState = (activeIndex) => {
      buttons.forEach((button) => {
        const index = Number(button.dataset.slide || 0);
        const active = index === activeIndex;
        button.classList.toggle('is-active', active);
        button.setAttribute('aria-pressed', active ? 'true' : 'false');
      });
    };

    buttons.forEach((button) => {
      if (button.dataset.bound === 'true') return;
      button.dataset.bound = 'true';
      button.addEventListener('click', () => {
        const index = Number(button.dataset.slide || 0);
        controller.slideTo(index);
      });
    });

    syncNavState(controller.getActiveIndex());
    controller.onChange(syncNavState);
  }

  function createSwiperController(swiper) {
    return {
      slideTo(index) {
        swiper.slideTo(index);
      },
      getActiveIndex() {
        return swiper.activeIndex;
      },
      onChange(callback) {
        swiper.on('slideChange', () => {
          callback(swiper.activeIndex);
        });
      }
    };
  }

  function createFallbackController(node, options) {
    if (node.classList.contains('bos-swiper-fallback-initialized')) return null;

    const wrapper = node.querySelector('.swiper-wrapper');
    if (!wrapper) return null;

    const slides = Array.from(wrapper.querySelectorAll('.swiper-slide'));
    if (!slides.length) return null;

    node.classList.add('bos-swiper-fallback-initialized');

    let activeIndex = clampIndex(options.initialSlide, slides.length);
    let slideWidth = 0;
    const listeners = [];

    const resolveSlidesPerView = () => {
      if (window.matchMedia('(min-width: 990px)').matches) {
        return Math.max(1, options.desktopSlides);
      }
      return Math.max(1, options.mobileSlides);
    };

    const resolveGap = () => {
      if (window.matchMedia('(min-width: 990px)').matches) {
        return Math.max(0, options.desktopGap);
      }
      return Math.max(0, options.mobileGap);
    };

    const updateTransform = () => {
      const gap = resolveGap();
      const shift = activeIndex * (slideWidth + gap);
      wrapper.style.transform = `translate3d(${-shift}px, 0, 0)`;
    };

    const applyLayout = () => {
      const slidesPerView = resolveSlidesPerView();
      const gap = resolveGap();
      const width = node.clientWidth;

      if (width <= 0) return;

      slideWidth = (width - gap * (slidesPerView - 1)) / slidesPerView;
      node.style.overflow = 'hidden';
      wrapper.style.display = 'flex';
      wrapper.style.gap = `${gap}px`;
      wrapper.style.transition = 'transform 500ms ease';
      wrapper.style.willChange = 'transform';

      slides.forEach((slide) => {
        slide.style.flex = `0 0 ${slideWidth}px`;
        slide.style.maxWidth = `${slideWidth}px`;
      });

      updateTransform();
    };

    const emitChange = () => {
      listeners.forEach((callback) => callback(activeIndex));
    };

    const controller = {
      slideTo(index) {
        const nextIndex = clampIndex(index, slides.length);
        if (nextIndex === activeIndex) return;
        activeIndex = nextIndex;
        updateTransform();
        emitChange();
      },
      getActiveIndex() {
        return activeIndex;
      },
      onChange(callback) {
        listeners.push(callback);
      }
    };

    applyLayout();
    window.addEventListener('resize', applyLayout, { passive: true });
    window.setTimeout(applyLayout, 0);
    emitChange();

    return controller;
  }

  function initSwiperNode(node) {
    if (!node || node.classList.contains('swiper-initialized') || node.classList.contains('bos-swiper-fallback-initialized')) return;

    const mobileSlides = Number(node.dataset.mobileSlides || 1);
    const desktopSlides = Number(node.dataset.desktopSlides || mobileSlides);
    const mobileGap = Number(node.dataset.mobileGap || 20);
    const desktopGap = Number(node.dataset.desktopGap || mobileGap);
    const centered = node.dataset.centered === 'true';
    const initialSlide = Number(node.dataset.initialSlide || 0);

    if (typeof Swiper === 'undefined') {
      const fallbackController = createFallbackController(node, {
        mobileSlides,
        desktopSlides,
        mobileGap,
        desktopGap,
        initialSlide: Number.isNaN(initialSlide) ? 0 : initialSlide
      });
      if (fallbackController) {
        bindExternalNav(fallbackController, node);
      }
      return;
    }

    const pagination = node.querySelector('.swiper-pagination');
    const swiperOptions = {
      slidesPerView: mobileSlides,
      spaceBetween: mobileGap,
      centeredSlides: centered,
      watchOverflow: true,
      speed: 500,
      initialSlide: Number.isNaN(initialSlide) ? 0 : initialSlide,
      breakpoints: {
        990: {
          slidesPerView: desktopSlides,
          spaceBetween: desktopGap,
          centeredSlides: centered
        }
      }
    };

    if (pagination) {
      swiperOptions.pagination = {
        el: pagination,
        clickable: true
      };
    }

    let swiper = null;
    try {
      swiper = new Swiper(node, swiperOptions);
    } catch (error) {
      const fallbackController = createFallbackController(node, {
        mobileSlides,
        desktopSlides,
        mobileGap,
        desktopGap,
        initialSlide: Number.isNaN(initialSlide) ? 0 : initialSlide
      });
      if (fallbackController) {
        bindExternalNav(fallbackController, node);
      }
      return;
    }

    bindExternalNav(createSwiperController(swiper), node);
  }

  function initSwipers(scope = document) {
    const nodes = scope.querySelectorAll('.bos-swiper');
    nodes.forEach(initSwiperNode);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initSwipers(document);
    });
  } else {
    initSwipers(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    if (event && event.target) {
      initSwipers(event.target);
    }
  });
})();
